import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tags, Loader2, RotateCcw, Save, Star } from "lucide-react";

const SEASONS = ["Primavera", "Estate", "Autunno", "Inverno"];
const SRC = { ai: "AI", shopify: "Tag Shopify", materiale: "Tessuto", manuale: "Correzione" };
const GENDERS = [["femmina", "Bambina"], ["maschio", "Bambino"], ["neutro", "Neutro"]];

const Row = ({ p, materialChoices, onSaved }) => {
  const [gender, setGender] = useState(p.gender);
  const [seasons, setSeasons] = useState(p.seasons || []);
  const [materials, setMaterials] = useState(p.materials || []);
  const [best, setBest] = useState(!!p.best_seller);
  const [busy, setBusy] = useState(false);
  const dirty = gender !== p.gender || seasons.join() !== (p.seasons || []).join() || materials.join() !== (p.materials || []).join() || best !== !!p.best_seller;
  const toggleMat = (m) => setMaterials((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : materialChoices.filter((x) => cur.includes(x) || x === m)));
  const shownMats = materialChoices.slice(0, 7).concat(materialChoices.slice(7).filter((m) => materials.includes(m) || p.material_auto === m));
  const toggle = (s) => setSeasons((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : SEASONS.filter((x) => cur.includes(x) || x === s)));

  const save = async () => {
    if (!seasons.length) return toast.error("Seleziona almeno una stagione");
    setBusy(true);
    try {
      const body = {};
      if (gender !== p.gender) body.gender = gender;
      if (seasons.join() !== (p.seasons || []).join()) body.seasons = seasons;
      if (materials.join() !== (p.materials || []).join()) body.materials = materials.length ? materials : null;
      if (best !== !!p.best_seller) body.best_seller = best;
      await api.put(`/admin/products/${p.id}/override`, body);
      toast.success("Correzione salvata");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try { await api.delete(`/admin/products/${p.id}/override`); toast.success("Ripristinati i valori automatici"); onSaved(); }
    catch { toast.error("Errore"); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col lg:flex-row lg:flex-wrap xl:flex-nowrap lg:items-center gap-3 py-3 border-b border-[#F4EFEA]" data-testid={`override-row-${p.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0 lg:min-w-[300px]">
        {p.image && <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover" />}
        <div className="min-w-0">
          <div className="text-sm text-[#2D2825] truncate">{p.title}</div>
          <div className="text-[11px] text-[#8C7F78]">
            {p.season_source === "manuale" ? <span className="text-[#C87D75] font-medium">Stagione corretta a mano</span> : `Stagione da: ${SRC[p.season_source] || "Tessuto"}`}
          </div>
          {p.created_at && <div className="text-[11px] text-[#8C7F78]" data-testid={`override-created-${p.id}`}>Creato il {new Date(p.created_at).toLocaleDateString("it-IT")}</div>}
          <div className="text-[11px] text-[#8C7F78]" data-testid={`override-material-source-${p.id}`}>
            Materiale: {p.material || "—"} · {p.material_source === "manuale" ? <span className="text-[#C87D75]">corretto a mano</span> : "rilevato in automatico"}
          </div>
        </div>
      </div>
      <select value={gender} onChange={(e) => setGender(e.target.value)} data-testid={`override-gender-${p.id}`}
        className="h-9 rounded-lg border border-[#EAE3DC] bg-[#FAF8F5] text-sm px-2">
        {GENDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <div className="flex flex-wrap gap-1.5">
        {SEASONS.map((s) => (
          <button key={s} onClick={() => toggle(s)} data-testid={`override-season-${p.id}-${s}`}
            className={`rounded-full px-3 py-1 text-xs border transition-colors ${seasons.includes(s) ? "bg-[#4E7363] text-white border-[#4E7363]" : "bg-white text-[#5C534E] border-[#EAE3DC]"}`}>{s}</button>
        ))}
      </div>
      <button onClick={() => setBest((v) => !v)} data-testid={`override-bestseller-${p.id}`} title="Best seller"
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] border transition-colors ${best ? "bg-[#E9C46A] text-[#2D2825] border-[#E9C46A]" : "bg-white text-[#8C7F78] border-[#EAE3DC]"}`}>
        <Star size={12} className={best ? "fill-[#2D2825]" : ""} /> Best seller
      </button>
      <div className="flex flex-wrap gap-1.5 lg:max-w-[280px]">
        {shownMats.map((m) => (
          <button key={m} onClick={() => toggleMat(m)} data-testid={`override-material-${p.id}-${m.replace(/\W+/g, "-")}`}
            className={`rounded-full px-2.5 py-1 text-[11px] border transition-colors ${materials.includes(m) ? "bg-[#C87D75] text-white border-[#C87D75]" : "bg-white text-[#5C534E] border-[#EAE3DC]"}`}>{m}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={!dirty || busy} data-testid={`override-save-${p.id}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#2D2825] text-white text-xs px-4 py-2 disabled:opacity-40">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salva
        </button>
        {p.overridden && (
          <button onClick={reset} disabled={busy} data-testid={`override-reset-${p.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#EAE3DC] text-xs px-3 py-2 text-[#5C534E]">
            <RotateCcw size={13} /> Automatico
          </button>
        )}
      </div>
    </div>
  );
};

export const ProductOverrides = () => {
  const [q, setQ] = useState("");
  const [onlyOv, setOnlyOv] = useState(false);
  const [gender, setGender] = useState("");
  const [material, setMaterial] = useState("");
  const [onlyBest, setOnlyBest] = useState(false);
  const [sort, setSort] = useState("newest");
  const [choices, setChoices] = useState([]);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/materials").then(({ data }) => setChoices(data.materials)).catch(() => {}); }, []);
  const [limit, setLimit] = useState(60);

  const load = () => api.get("/admin/products", { params: { q, gender: gender || undefined, material: material || undefined, only_best: onlyBest || undefined, only_overridden: onlyOv || undefined, sort: sort || undefined, limit } })
    .then(({ data }) => setData(data)).catch(() => setData({ products: [], total: 0 }));
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, onlyOv, gender, material, onlyBest, sort, limit]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLimit(60); }, [q, onlyOv, gender, material, onlyBest, sort]);

  return (
    <div className="rounded-2xl bg-white border border-[#EAE3DC] p-6 mb-6" data-testid="product-overrides">
      <div className="flex items-center gap-2 mb-1">
        <Tags size={18} className="text-[#C87D75]" />
        <h3 className="font-display font-medium text-[#2D2825]">Correggi sesso, stagione e materiale dei prodotti</h3>
      </div>
      <p className="text-xs text-[#8C7F78] mb-4">La tua correzione vince su tutto: AI, tessuto e tag Shopify.</p>
      <div className="flex flex-col sm:flex-row gap-3 mb-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un prodotto…" data-testid="override-search" className="rounded-xl" />
        <select value={gender} onChange={(e) => setGender(e.target.value)} data-testid="override-filter-gender"
          className="h-10 rounded-xl border border-[#EAE3DC] bg-white text-sm px-3">
          <option value="">Tutti i sessi</option>
          {GENDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={material} onChange={(e) => setMaterial(e.target.value)} data-testid="override-filter-material"
          className="h-10 rounded-xl border border-[#EAE3DC] bg-white text-sm px-3">
          <option value="">Tutti i materiali</option>
          {choices.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="override-sort"
          className="h-10 rounded-xl border border-[#EAE3DC] bg-white text-sm px-3">
          <option value="newest">Più recenti</option>
          <option value="oldest">Meno recenti</option>
          <option value="">Ordine catalogo</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-[#5C534E] whitespace-nowrap">
          <input type="checkbox" checked={onlyBest} onChange={(e) => setOnlyBest(e.target.checked)} data-testid="override-filter-best" />
          Solo best seller
        </label>
        <label className="flex items-center gap-2 text-sm text-[#5C534E] whitespace-nowrap">
          <input type="checkbox" checked={onlyOv} onChange={(e) => setOnlyOv(e.target.checked)} data-testid="override-only" /> Solo corretti
        </label>
      </div>
      {!data ? <div className="py-6 text-center text-[#8C7F78]"><Loader2 className="animate-spin inline" size={16} /></div> : (
        <>
          <div className="text-xs text-[#8C7F78] mb-1" data-testid="override-count">Mostrati {data.products.length} di {data.total} prodotti</div>
          <div className="max-h-[640px] overflow-y-auto pr-1">
            {data.products.map((p) => <Row key={`${p.id}-${p.gender}-${(p.seasons || []).join()}-${(p.materials || []).join()}-${p.best_seller}-${p.overridden}`} p={p} materialChoices={choices} onSaved={load} />)}
            {data.products.length < data.total && (
              <div className="flex flex-wrap justify-center gap-2 py-4">
                <button onClick={() => setLimit((l) => l + 60)} data-testid="override-load-more"
                  className="rounded-full border border-[#D2C5B9] text-[#5C534E] hover:bg-[#FAF8F5] px-5 py-2 text-sm">Mostra altri 60</button>
                <button onClick={() => setLimit(500)} data-testid="override-load-all"
                  className="rounded-full bg-[#2D2825] text-white px-5 py-2 text-sm">Mostra tutti ({data.total})</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
